/**
 * Turning a buffer of microphone samples into a trustworthy frequency.
 *
 * Two stages: the McLeod pitch method (via pitchy) finds the right cycle, then
 * phase refinement finds the right frequency. MPM alone lands within a cent or
 * so, which is fine for a guitar and useless for an organ; the refinement pulls
 * that down to hundredths of a cent on a steady pipe.
 */

import { PitchDetector } from "pitchy";
import { correctSubOctave, dbfs, median, refineFrequency, rms } from "./dsp.js";

export interface Reading {
  /** Refined fundamental frequency in Hz. */
  readonly frequency: number;
  /** MPM clarity, 0..1. Below about 0.9 the reading is not worth trusting. */
  readonly clarity: number;
  /** Input level in dBFS. */
  readonly levelDb: number;
}

export interface AnalyserOptions {
  /** Lowest fundamental to consider. 32' C is about 16 Hz. */
  readonly minHz?: number;
  /** Highest fundamental to consider. */
  readonly maxHz?: number;
  /** Reject buffers quieter than this, in dBFS. */
  readonly minLevelDb?: number;
  /** Reject MPM results less clear than this. */
  readonly minClarity?: number;
  /** Apply the sub-octave guard. Disable when the octave is locked by hand. */
  readonly guardSubOctave?: boolean;
}

const DEFAULTS = {
  minHz: 25,
  maxHz: 5000,
  minLevelDb: -60,
  minClarity: 0.9,
  guardSubOctave: true,
} satisfies Required<AnalyserOptions>;

export class ToneAnalyser {
  private readonly detector: PitchDetector<Float32Array>;
  private options: Required<AnalyserOptions>;

  constructor(bufferSize: number, options: AnalyserOptions = {}) {
    this.detector = PitchDetector.forFloat32Array(bufferSize);
    this.options = { ...DEFAULTS, ...options };
    this.detector.minVolumeDecibels = this.options.minLevelDb;
  }

  configure(options: AnalyserOptions): void {
    this.options = { ...this.options, ...options };
    this.detector.minVolumeDecibels = this.options.minLevelDb;
  }

  analyse(buffer: Float32Array, sampleRate: number): Reading | null {
    const levelDb = dbfs(rms(buffer));
    if (levelDb < this.options.minLevelDb) return null;

    const [coarse, clarity] = this.detector.findPitch(buffer, sampleRate);
    if (!(coarse > 0) || clarity < this.options.minClarity) return null;

    const guarded = this.options.guardSubOctave
      ? correctSubOctave(buffer, sampleRate, coarse, this.options.minHz)
      : coarse;

    const frequency = refineFrequency(buffer, sampleRate, guarded);
    if (!Number.isFinite(frequency)) return null;
    if (frequency < this.options.minHz || frequency > this.options.maxHz) return null;

    return { frequency, clarity, levelDb };
  }
}

export interface SmoothedPitch {
  /** Median of the recent window, in Hz. */
  readonly frequency: number;
  /** Spread of the window in cents. Small means the pipe has settled. */
  readonly spreadCents: number;
  /** True once the window is full and tight enough to act on. */
  readonly settled: boolean;
  readonly samples: number;
}

/**
 * Rolling median over recent readings.
 *
 * A median rather than a mean because the occasional octave slip or transient
 * would drag an average across the display and take seconds to come back; the
 * median simply ignores it. The spread doubles as the "is it holding still yet"
 * indicator, which is the thing you actually wait for before touching a pipe.
 */
export class PitchSmoother {
  private readonly window: number[] = [];

  constructor(
    private readonly size = 9,
    private readonly settleCents = 1.5,
    /** Discard the window when a reading jumps further than this, in cents. */
    private readonly resetCents = 120,
  ) {}

  clear(): void {
    this.window.length = 0;
  }

  push(frequency: number): void {
    if (!(frequency > 0)) return;
    const last = this.window[this.window.length - 1];
    if (last !== undefined && Math.abs(1200 * Math.log2(frequency / last)) > this.resetCents) {
      // A jump this large is a different pipe, not a wobble. Start again rather
      // than averaging across two notes and displaying neither.
      this.window.length = 0;
    }
    this.window.push(frequency);
    if (this.window.length > this.size) this.window.shift();
  }

  read(): SmoothedPitch | null {
    if (this.window.length === 0) return null;
    const frequency = median(this.window);
    let lo = Infinity;
    let hi = -Infinity;
    for (const value of this.window) {
      if (value < lo) lo = value;
      if (value > hi) hi = value;
    }
    const spreadCents = lo > 0 ? 1200 * Math.log2(hi / lo) : Infinity;
    return {
      frequency,
      spreadCents,
      settled: this.window.length >= this.size && spreadCents <= this.settleCents,
      samples: this.window.length,
    };
  }
}
